import type { ChannelSlice } from '../ChannelSlice';
import type { TradingViewChartWidgetAdapter } from '../TradingViewChartWidgetAdapter';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { optional } from 'inversify';
import { ReactiveInjectable, inject, injectable } from 'src/features/ioc';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import type { TradingViewChartWidgetModel } from 'src/models/TradingViewChartWidgetModel';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

@injectable()
export class TradingViewChartWidgetModelImpl extends ReactiveInjectable implements TradingViewChartWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;
    #adapter: TradingViewChartWidgetAdapter;

    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity']);
    #instrument: Signal<string> = signal('');
    #chartState: Signal<object | undefined> = signal(undefined);
    #interval: Signal<string | undefined> = signal(undefined);
    #timezone: Signal<string | undefined> = signal(undefined);

    headerElements!: WidgetHeaderElement[];

    eventBus!: TradingViewChartWidgetModel['eventBus'];

    #buildEventBus(): TradingViewChartWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                .addEvent('widget::instrumentChange', (payload) => {
                    this.setInstrument(payload.instrument);
                })
                .addSignal('widget::instrument', () => this.#instrument)
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus);
    }

    constructor(
        @inject('TradingViewChartWidgetAdapter') adapter: TradingViewChartWidgetAdapter,
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        this.#adapter = adapter;
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        this.eventBus = this.#buildEventBus();
    }

    get instrument(): ReadonlySignal<string> {
        return this.#instrument;
    }

    setInstrument(instrument: string) {
        this.#instrument.value = instrument;
        this.#adapter.persist({ symbol: instrument });
    }

    get chartState(): ReadonlySignal<object | undefined> {
        return this.#chartState as ReadonlySignal<object | undefined>;
    }

    get interval(): ReadonlySignal<string | undefined> {
        return this.#interval as ReadonlySignal<string | undefined>;
    }

    get timezone(): ReadonlySignal<string | undefined> {
        return this.#timezone as ReadonlySignal<string | undefined>;
    }

    updateChartState(chartState: object, interval: string, timezone: string) {
        this.#chartState.value = chartState;
        this.#interval.value = interval;
        this.#timezone.value = timezone;
        this.#adapter.persist({ chartState, interval, timezone });
    }

    init(tabId?: string) {
        if (tabId) {
            // Initialize slices (workspace mode only)
            this.#channelSlice.init(tabId);
            this.#widgetTabSlice.init(tabId);
            this.disposableStack.use(this.#channelSlice);
            this.disposableStack.use(this.#widgetTabSlice);
        }

        // Initialize header elements
        const baseElements = tabId ? COMMON_HEADER_ELEMENTS : STANDALONE_COMMON_HEADER_ELEMENTS;
        this.headerElements = [...baseElements, { type: 'symbolPicker', position: 30, priority: 5, variant: 'light' }];

        // Sync initial state from adapter store
        const state = this.#adapter.getState();
        this.#instrument.value = state.symbol;
        this.#chartState.value = state.chartState;
        this.#interval.value = state.interval;
        this.#timezone.value = state.timezone;

        // Subscribe to external state changes
        this.disposableStack.defer(
            this.#adapter.subscribe((state) => {
                this.#instrument.value = state.symbol;
                this.#chartState.value = state.chartState;
                this.#interval.value = state.interval;
                this.#timezone.value = state.timezone;
            }),
        );
    }
}
